import { SendEmailAuthLinkError } from "@redotech/customer-portal-sdk/rpc/schema/send-email-auth-link";
import { emailIsValid } from "@redotech/email";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ClientError } from "@redotech/rpc/errors";
import { useContext, useState } from "react";
import { SettingsContext } from "../contexts/settings";
import {
  getHeaderText,
  getLoginGiftText,
  sendEmailAuthLinkErrorToText,
} from "./dynamic-text-utils";

export function useEmailLink({
  nextUrl,
  title,
}: {
  nextUrl?: string;
  title?: string;
}) {
  const settings = useContext(SettingsContext);

  const rpcClient = useRequiredContext(RpcClientContext);
  const [hasResentEmail, setHasResentEmail] = useState(false);
  const [emailValidation, setEmailValidation] = useState<string | undefined>(
    undefined,
  );
  const [emailInput, setEmailInput] = useState("");

  const onEmailChange = useHandler((email: string) => {
    if (!email) {
      setEmailValidation("Email is required");
    } else if (emailIsValid(email)) {
      setEmailValidation(undefined);
    } else {
      setEmailValidation("Invalid email");
    }
    setEmailInput(email);
  });
  const [sendLinkLoadState, sendLink] = useTriggerLoad(async () => {
    try {
      return await rpcClient.sendEmailAuthLink({
        email: emailInput,
        baseUrl: location.href,
        nextUrl,
      });
    } catch (e) {
      if (e instanceof ClientError) {
        const rpcError = e as ClientError<SendEmailAuthLinkError>;
        if (rpcError.code) {
          throw new Error(sendEmailAuthLinkErrorToText[rpcError.code]);
        }
      }

      throw new Error("Something went wrong sending the email.");
    }
  });

  const [resendTriggerLink, resendLink] = useTriggerLoad(async () => {
    setHasResentEmail(true);
    return await rpcClient.sendEmailAuthLink({
      email: emailInput,
      baseUrl: location.href,
      nextUrl,
    });
  });

  return {
    texts: {
      emailLabel: "Email",
      emailHint: "Enter your email",
      sendLink: sendLinkLoadState.pending ? "Sending..." : "Send link",
      title: title ?? getHeaderText(settings),
      subtitle: "Enter your email to receive a unique login link.",
      merchantName: settings?.merchantName || "Unknown Merchant",
      emailConfirmationTitle: "Check your email",
      emailConfirmationSubtitle: `We sent an email to ${emailInput}. Open the email and click the link to login to our customer portal.`,
      didNotReceiveEmail: "Didn't receive an email?",
      resendLink: hasResentEmail
        ? "We sent another link to your email"
        : "Resend email",
      giftButton: getLoginGiftText(settings),
      dontHaveEmail: "Log in with an order number",
    },
    onEmailChange,
    onSendLink: sendLink,
    onResendLink: resendLink,
    emailInput,
    sendLinkDisabled:
      sendLinkLoadState.pending ||
      emailValidation !== undefined ||
      emailInput === "",
    emailValidationError: emailValidation,
    resendEmailError: resendTriggerLink.error?.message,
    sendEmailError: sendLinkLoadState.error?.message,
    showEmailConfirmationPage:
      sendLinkLoadState.value !== undefined &&
      sendLinkLoadState.error === undefined,
    canResend: !hasResentEmail,
    socialLoginOptions: settings?.theme?.socialLoginOptions,
  };
}
